const BASE_URL =
  process.env.REACT_APP_API_BASE ?? 'https://api.findyournextread.com/api';
const TOKEN_KEY = 'auth_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);

export const setToken = (token: string) =>
  localStorage.setItem(TOKEN_KEY, token);

export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(
    public status: number,
    statusText: string,
  ) {
    super(`API error ${status}: ${statusText}`);
  }
}

const request = async <T>(
  path: string,
  options: RequestInit = {},
  { auth = true }: { auth?: boolean } = {},
): Promise<T> => {
  const token = auth ? getToken() : null;
  const isUpload = options.body instanceof FormData;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(isUpload ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (!res.ok) {
    throw new ApiError(res.status, res.statusText);
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  return res.json();
};

export type ApiAttachment = {
  id: number;
  url: string;
  /** WebP renditions keyed by width in px, e.g. { '200': url, '400': url, '800': url }. */
  variants?: Record<string, string>;
  original_name: string;
  mime_type: string;
  size: number;
};

export type ApiBook = {
  id: number;
  title: string;
  author: string;
  cover_color: string;
  cover_text_color: string;
  categories: (string | ApiCategory)[] | null;
  description: string | Record<string, unknown> | null;
  published: string | null;
  pages: number | null;
  language: string | null;
  isbn: string | null;
  cover?: ApiAttachment | null;
  created_at: string;
  updated_at: string;
  ratings_sum: number | null;
  ratings_count: number | null;
  rating_avg: number | string | null;
  next_books?: ApiBook[];
};

export type ApiPaginatedResponse<T> = {
  current_page: number;
  data: T[];
  last_page: number;
  total: number;
  next_page_url: string | null;
  prev_page_url: string | null;
};

export type LoginResponse = {
  user: { id: number; name: string; email: string };
  token: string;
};

export const apiLogin = (
  email: string,
  password: string,
): Promise<LoginResponse> =>
  request<LoginResponse>(
    '/login',
    {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    },
    { auth: false },
  );

export const apiLogout = (): Promise<unknown> =>
  request('/logout', { method: 'POST' });

export type ApiReview = {
  id: number;
  author: string;
  text: string;
  book_id: number;
  rating: number;
  language: string | null;
  created_at: string;
  updated_at: string;
};

export type BooksQuery = {
  search?: string;
  perPage?: number;
  sort?: string;
  include?: string;
};

export const apiGetBooks = (
  page = 1,
  { search, perPage, sort, include }: BooksQuery = {},
): Promise<ApiPaginatedResponse<ApiBook>> => {
  const params = new URLSearchParams({ page: String(page) });

  if (include) {
    params.set('include', include);
  }

  if (search) {
    params.set('filter[search]', search);
  }
  if (perPage) {
    params.set('per_page', String(perPage));
  }
  if (sort) {
    params.set('sort', sort);
  }

  return request<ApiPaginatedResponse<ApiBook>>(`/books?${params.toString()}`);
};

export const apiGetBook = (id: number, include?: string): Promise<ApiBook> =>
  request<ApiBook>(
    `/books/${id}${include ? `?include=${encodeURIComponent(include)}` : ''}`,
  );

export type ApiBookCategory = {
  id: number;
  name: string;
  books_count?: number;
  created_at?: string;
  updated_at?: string;
  pivot?: { book_id: number; category_id: number };
};

export type ApiCategory = {
  id: number;
  name: string;
  books_count?: number;
  created_at: string;
  updated_at: string;
};

export const apiGetCategories = (
  page = 1,
): Promise<ApiPaginatedResponse<ApiCategory>> =>
  request<ApiPaginatedResponse<ApiCategory>>(`/categories?page=${page}`);

export type ReviewsQuery = {
  author?: string;
  bookId?: number;
  perPage?: number;
};

export const apiGetReviews = (
  page = 1,
  { author, bookId, perPage }: ReviewsQuery = {},
): Promise<ApiPaginatedResponse<ApiReview>> => {
  const params = new URLSearchParams({ page: String(page) });

  if (author) {
    params.set('author', author);
  }
  if (bookId) {
    params.set('book_id', String(bookId));
  }
  if (perPage) {
    params.set('per_page', String(perPage));
  }

  return request<ApiPaginatedResponse<ApiReview>>(
    `/reviews?${params.toString()}`,
  );
};

export type CreateReviewPayload = {
  author: string;
  text: string;
  rating: number;
  book_id: number;
  language?: string;
};

export const apiCreateReview = (
  payload: CreateReviewPayload,
): Promise<ApiReview> =>
  request<ApiReview>('/reviews', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export type SubmitUserReviewPayload = {
  author: string;
  text: string;
  rating: number;
  book_id: number;
  language: string;
};

export const apiSubmitUserReview = (
  payload: SubmitUserReviewPayload,
): Promise<unknown> =>
  request(
    '/user-reviews',
    { method: 'POST', body: JSON.stringify(payload) },
    { auth: false },
  );

export type ApiUserReview = {
  id: number;
  author: string;
  text: string;
  book_id: number;
  rating: number;
  language: string | null;
  status: 'pending' | 'approved' | 'rejected';
  moderation_note: string | null;
  moderated_at: string | null;
  moderated_by: number | null;
  created_at: string;
  updated_at: string;
  book?: { id: number; title: string; author: string } | null;
};

export type UserReviewsQuery = {
  status?: ApiUserReview['status'];
  bookId?: number;
  author?: string;
  perPage?: number;
};

export const apiGetUserReviews = (
  page = 1,
  { status, bookId, author, perPage }: UserReviewsQuery = {},
): Promise<ApiPaginatedResponse<ApiUserReview>> => {
  const params = new URLSearchParams({ page: String(page) });

  if (status) {
    params.set('status', status);
  }
  if (bookId) {
    params.set('book_id', String(bookId));
  }
  if (author) {
    params.set('author', author);
  }
  if (perPage) {
    params.set('per_page', String(perPage));
  }

  return request<ApiPaginatedResponse<ApiUserReview>>(
    `/user-reviews?${params.toString()}`,
  );
};

export const apiUpdateUserReview = (
  id: number,
  payload: Partial<SubmitUserReviewPayload> & {
    status?: ApiUserReview['status'];
    moderation_note?: string | null;
  },
): Promise<ApiUserReview> =>
  request<ApiUserReview>(`/user-reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const apiDeleteUserReview = (id: number): Promise<void> =>
  request<void>(`/user-reviews/${id}`, { method: 'DELETE' });

export const apiDeleteReview = (id: number): Promise<void> =>
  request<void>(`/reviews/${id}`, { method: 'DELETE' });

export const apiGetReview = (id: number): Promise<ApiReview> =>
  request<ApiReview>(`/reviews/${id}`);

export const apiUpdateReview = (
  id: number,
  payload: Partial<CreateReviewPayload>,
): Promise<ApiReview> =>
  request<ApiReview>(`/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export type CreateBookPayload = {
  title: string;
  author: string;
  published?: string | null;
  pages?: number | null;
  language?: string | null;
  category_ids?: number[];
  description?: { en: string } | null;
  cover_color: string;
  cover_text_color: string;
  isbn?: string | null;
};

export const apiCreateBook = (payload: CreateBookPayload): Promise<ApiBook> =>
  request<ApiBook>('/books', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const apiUpdateBook = (
  id: number,
  payload: Partial<CreateBookPayload>,
): Promise<ApiBook> =>
  request<ApiBook>(`/books/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

export const apiDeleteBook = (id: number): Promise<unknown> =>
  request(`/books/${id}`, { method: 'DELETE' });

export const apiUploadBookCover = (
  id: number,
  file: Blob,
): Promise<ApiBook> => {
  const body = new FormData();
  body.append('cover', file);

  return request<ApiBook>(`/books/${id}/cover`, { method: 'POST', body });
};

export const apiDeleteBookCover = (id: number): Promise<unknown> =>
  request(`/books/${id}/cover`, { method: 'DELETE' });

export const apiRestoreBook = (id: number): Promise<ApiBook> =>
  request<ApiBook>(`/books/${id}/restore`, { method: 'POST' });

export const apiSetNextBooks = (
  id: number,
  nextBookIds: number[],
): Promise<unknown> =>
  request(`/books/${id}/next-books`, {
    method: 'POST',
    body: JSON.stringify({ next_book_ids: nextBookIds }),
  });

export const apiRemoveNextBook = (
  id: number,
  nextBookId: number,
): Promise<unknown> =>
  request(`/books/${id}/next-books/${nextBookId}`, { method: 'DELETE' });

export type ConsentCategory = 'necessary' | 'statistics' | 'marketing';

export type StoreConsentPayload = {
  subject_id: string;
  action: 'granted' | 'withdrawn' | 'updated';
  categories: ConsentCategory[];
  consent_version: string;
  source?: 'banner_first' | 'banner_reopen' | 'settings_page';
  vendors?: string[] | null;
  purpose?: string | null;
};

export type ApiConsent = {
  consent_id: string;
  subject_id: string;
  action: string;
  categories: ConsentCategory[];
  vendors: string[] | null;
  purpose: string | null;
  consent_version: string;
  source: string | null;
  expires_at: string;
  created_at: string | null;
};

export const apiStoreConsent = (
  payload: StoreConsentPayload,
): Promise<{ data: ApiConsent }> =>
  request<{ data: ApiConsent }>('/consent', {
    method: 'POST',
    body: JSON.stringify(payload),
    keepalive: true,
  });

export const apiGetConsent = (
  subjectId: string,
): Promise<{ data: ApiConsent }> =>
  request<{ data: ApiConsent }>(`/consent/${encodeURIComponent(subjectId)}`);

export const apiWithdrawConsent = (
  subjectId: string,
  reason?: string,
): Promise<unknown> =>
  request('/consent/withdraw', {
    method: 'POST',
    body: JSON.stringify({ subject_id: subjectId, reason }),
    keepalive: true,
  });

export const apiTrackImpression = (id: number): Promise<unknown> =>
  request(`/events/impressions/${id}`, { method: 'POST' });

export const apiRateBook = (
  id: number,
  rating: number,
  visitorId: string,
): Promise<unknown> =>
  request(`/events/ratings/${id}`, {
    method: 'POST',
    body: JSON.stringify({ rating, visitor_id: visitorId }),
  });
